import { DarkMode, Email, LightMode, Person, SettingsBrightness } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useCurrentUser } from 'api';
import useExperiments from 'api/experiments';
import { withoutError } from 'api/utils';
import { ThemeContext } from 'App';
import React, { useContext } from 'react';

import { useHeader } from './useHeader';

export default function UserProfileMenu() {
  const userProfile = withoutError(useCurrentUser());
  const { isExperimentEnabled } = useExperiments();
  const { open, anchorEl, handleClose, onPressProfile } = useHeader();
  const themeContext = useContext(ThemeContext);

  const handleModeChange = (_event: React.SyntheticEvent, newMode: 'light' | 'dark' | 'system') => {
    if (themeContext.mode) {
      themeContext.mode.onChange(newMode);
      window.localStorage.setItem('theme-mode', newMode);
    }
  };

  const handleVersionChange = () => {
    const newVersion = themeContext.version?.current === 'newUi' ? 'default' : 'newUi';
    if (themeContext.version) {
      themeContext.version.onChange(newVersion);
      window.localStorage.setItem('theme-version', newVersion);
    }
  };

  return (
    <>
      <IconButton color="primary" onClick={(e: any) => onPressProfile(e)} data-testid="HeaderProfileIconButton">
        <Person />
      </IconButton>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        MenuListProps={{
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem sx={{ pointerEvents: 'none' }}>
          <ListItemText
            primary={`${userProfile?.firstName} ${userProfile?.lastName}`}
            secondary={userProfile?.organization}
          />
        </MenuItem>
        <ListItemButton onClick={() => window.open('mailto:support@sentinelinsights.com')}>
          <ListItemIcon>
            <Email />
          </ListItemIcon>
          <ListItemText primary="Contact Us" />
        </ListItemButton>
        <Divider />
        <Box sx={{ px: 1, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Theme Mode
          </Typography>
          <Tabs
            value={themeContext.mode?.current || 'light'}
            onChange={handleModeChange}
            variant="fullWidth"
            sx={{ minHeight: 'auto' }}
          >
            <Tab icon={<LightMode fontSize="small" />} label="Light" value="light" sx={{ minHeight: 'auto', py: 1 }} />
            <Tab icon={<DarkMode fontSize="small" />} label="Dark" value="dark" sx={{ minHeight: 'auto', py: 1 }} />
            <Tab
              icon={<SettingsBrightness fontSize="small" />}
              label="System"
              value="system"
              sx={{ minHeight: 'auto', py: 1 }}
            />
          </Tabs>
        </Box>
        <Divider />
        <MenuItem
          sx={{
            alignItems: 'center',
          }}
          onClick={handleVersionChange}
        >
          <Typography sx={{ flex: 1 }}>Use New UI</Typography>
          <Checkbox
            sx={{
              pointerEvents: 'none',
            }}
            checked={themeContext.version?.current === 'newUi'}
            data-testid="new-ui-toggle"
          />
        </MenuItem>
        {isExperimentEnabled('user.isInternal') && (
          <MenuItem
            data-testid="internal-tools-toggle"
            onClick={() => {
              const prev = window.localStorage.getItem('showInternalTools');
              window.localStorage.setItem('showInternalTools', prev === 'true' ? 'false' : 'true');
              window.location.reload();
            }}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>Show Internal Tools</Typography>
            <Switch checked={window.localStorage.getItem('showInternalTools') === 'true'} />
          </MenuItem>
        )}
        {isExperimentEnabled('user.isInternal') && (
          <Stack sx={{ px: 1, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Timezone: {userProfile?.preferences?.timezone ?? 'N/A'}
            </Typography>
          </Stack>
        )}
      </Menu>
    </>
  );
}
